import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ARTICLE, JOURNAL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function Body({ text }) {
  const paras = (text || "").split(/\n\s*\n/).filter((p) => p.trim());
  return (
    <div className="prose-editorial">
      {paras.map((p, i) => (
        <p
          key={`${i}:${p.slice(0, 32)}`}
          className="text-ink text-[17.5px] md:text-[19px] leading-[1.75] mb-6 md:mb-7"
        >
          {p}
        </p>
      ))}
    </div>
  );
}

function RelatedCard({ a }) {
  return (
    <Link to={`/journal/${a.slug}`} className="group block">
      <div className="aspect-[4/3] bg-[#f4f4f4] overflow-hidden">
        {a.cover_image && (
          <img
            src={a.cover_image}
            alt={a.title}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            referrerPolicy="no-referrer"
          />
        )}
      </div>
      <div className="pt-4">
        <div className="eyebrow eyebrow-denim mb-2">{a.category}</div>
        <h4 className="font-display font-extrabold text-[18px] md:text-[20px] tracking-[-0.02em] leading-[1.2] text-ink group-hover:text-denim-bnm transition-colors">
          {a.title}
        </h4>
      </div>
    </Link>
  );
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setArticle(null);
    setRelated([]);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/journal/articles/${slug}`);
        if (!alive) return;
        setArticle(data?.article || null);
        setRelated(data?.related || []);
      } catch (e) {
        if (!alive) return;
        setNotFound(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const shareOnWhatsApp = () => {
    if (!article) return;
    const url = `${window.location.origin}/journal/${article.slug}`;
    const msg = `${article.title}, from Brand New Money Journal.\n${url}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const copyLink = async () => {
    if (!article) return;
    const url = `${window.location.origin}/journal/${article.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard blocked by browser permissions, fall back silently.
      console.warn("copy to clipboard failed:", err?.message);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white text-ink flex flex-col">
        <Nav />
        <div className="flex-1 max-w-[720px] mx-auto px-6 md:px-10 py-32 text-center">
          <div className="eyebrow eyebrow-denim mb-6">Not found</div>
          <h1 className="font-display font-extrabold text-[36px] md:text-[52px] tracking-[-0.04em] leading-[1.05] text-ink">
            This story isn&rsquo;t in the archive.
          </h1>
          <Link
            to="/journal"
            className="btn-denim mt-10 inline-flex"
            data-testid={ARTICLE.back}
          >
            <span>Back to the Journal</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white text-ink flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center text-muted-ink text-[14px]">
          Loading story…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={ARTICLE.page}>
      <Nav />

      {/* Header */}
      <header className="max-w-[900px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-8">
        <button
          type="button"
          onClick={() => navigate("/journal")}
          className="inline-flex items-center gap-2 text-muted-ink text-[13px] hover:text-ink transition-colors mb-10"
          data-testid={ARTICLE.back}
        >
          <ArrowLeft className="w-4 h-4" /> Back to the Journal
        </button>
        <div className="eyebrow eyebrow-denim mb-6">{article.category}</div>
        <h1
          data-testid={ARTICLE.title}
          className="font-display font-extrabold text-ink leading-[1.02] tracking-[-0.04em] text-[36px] sm:text-[52px] md:text-[72px]"
        >
          {article.title}
        </h1>
        {article.dek && (
          <p className="text-muted-ink text-[18px] md:text-[21px] leading-[1.55] mt-6 md:mt-8 max-w-[720px]">
            {article.dek}
          </p>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-[12.5px] text-muted-ink tracking-[0.02em]">
          <span>{article.author || "Brand New Money Editorial"}</span>
          <span className="w-1 h-1 rounded-full bg-muted-ink/50" />
          <span>{formatDate(article.published_at || article.created_at)}</span>
          <span className="w-1 h-1 rounded-full bg-muted-ink/50" />
          <span>{article.reading_time || 3} min read</span>
          {article.brand && (
            <>
              <span className="w-1 h-1 rounded-full bg-muted-ink/50" />
              <span className="italic">{article.brand}</span>
            </>
          )}
        </div>
      </header>

      {/* Cover */}
      {article.cover_image && (
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 mb-14 md:mb-20">
          <div className="aspect-[16/9] overflow-hidden bg-[#f4f4f4]">
            <img
              src={article.cover_image}
              alt={article.title}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <article className="max-w-[720px] mx-auto px-6 md:px-10 pb-8">
        <Body text={article.body} />
      </article>

      {/* Producer's Take */}
      {article.producer_take && (
        <section
          data-testid={ARTICLE.producerTake}
          className="border-t border-b border-bnm bg-[#fafafa] mt-8 md:mt-14"
        >
          <div className="max-w-[720px] mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="flex items-center gap-4 mb-8">
              <div className="font-display font-extrabold text-[52px] md:text-[80px] leading-none tracking-[-0.05em] text-denim-bnm">
                ✱
              </div>
              <div>
                <div className="eyebrow eyebrow-denim mb-2">Producer&rsquo;s Take</div>
                <h2 className="font-display font-extrabold text-[26px] md:text-[36px] tracking-[-0.03em] leading-[1.1] text-ink">
                  What <span className="font-serif-italic font-light">we&rsquo;d</span> tell you.
                </h2>
              </div>
            </div>
            <Body text={article.producer_take} />
          </div>
        </section>
      )}

      {/* Share row */}
      <section className="max-w-[720px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-muted-ink eyebrow eyebrow-muted mr-2">
            Share this story
          </span>
          <button
            type="button"
            onClick={shareOnWhatsApp}
            className="pill inline-flex items-center gap-2"
            data-testid={ARTICLE.shareWhatsapp}
          >
            <WhatsAppIcon className="text-denim-bnm" />
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="pill inline-flex items-center gap-2"
            data-testid={ARTICLE.shareCopy}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-denim-bnm" />
                <span>Link copied</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Copy link</span>
              </>
            )}
          </button>
        </div>
      </section>

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <section className="max-w-[720px] mx-auto px-6 md:px-10 pb-14 md:pb-20">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span
                key={t}
                className="px-3.5 py-1.5 rounded-full border border-bnm text-[12.5px] text-muted-ink"
              >
                {t}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-bnm" data-testid={ARTICLE.related}>
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-baseline justify-between mb-10">
              <h3 className="font-display font-extrabold text-[24px] md:text-[36px] tracking-[-0.03em] leading-[1.1] text-ink">
                Keep <span className="font-serif-italic font-light">reading</span>.
              </h3>
              <Link
                to="/journal"
                className="text-[13.5px] text-muted-ink hover:text-ink inline-flex items-center gap-2"
              >
                All stories <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {related.map((r) => (
                <RelatedCard key={r.id} a={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA (light) */}
      <section className="border-t border-bnm bg-[#fafafa]">
        <div className="max-w-[720px] mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
          <div className="eyebrow eyebrow-denim mb-4">Never miss a story</div>
          <h3 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.03em] leading-[1.05] text-ink">
            The Journal, in your inbox.
          </h3>
          <Link
            to="/journal#today"
            className="btn-ghost mt-8 inline-flex"
            data-testid={JOURNAL.heroCta}
          >
            <span>Subscribe on the Journal</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
